/**
 * Hour-of-day helpers for the LAE school modal's "Best hours" and "Moves by hour" blocks.
 *
 * Port of LAE `features/productivity/hourPattern.js` (`hourLabel`, `hourSpan`, `peakSpan`) plus the
 * pure halves of `charts/OutcomeHours.jsx` (`rows` → `outcomeRows`, `bestWindow` → `bestOutcomeWindow`)
 * and `charts/WeekdayHourHeatmap.jsx` (`stepOf` → `heatStep`). One copy, shared by `OutcomeHours.tsx`
 * and the StatusHours block in `SchoolModal.tsx`, so the two never disagree about "the busiest stretch".
 *
 * Deliberate differences from LAE:
 * - `hourPattern()` (calls + connected per hour, the core span) is NOT ported: it fed `HourBars`, which
 *   belongs to the counsellor modal and is out of scope.
 *
 * Hours are India time (the upstream window is locked to Asia/Kolkata). Nothing here converts anything.
 * Windows never wrap past midnight: a wrapping band cannot be drawn as one block.
 */

export const HOURS_IN_DAY = 24;

/** The busiest stretch is reported over at most three hours. */
const PEAK_SPAN = 3;

/**
 * A stretch is only named when the busiest hour beats the mean over active hours by this factor;
 * without it an even day gets a confident "busiest 00:00–03:00" from the earliest of many tied windows.
 */
const PEAK_LIFT = 1.25;

/** A 3-hour best window replaces the 2-hour one only when it holds at least this much more. */
const WIDEN_GAIN = 1.15;

/** Non-finite and negative values count as 0 (a count, never displayed through this). */
const num = (v: unknown): number => {
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? Math.max(0, n) : 0;
};

/** 11 → '11:00'. Hour 24 is the midnight that closes a window, printed '24:00'. */
export function hourLabel(h: number): string {
  return `${String(h).padStart(2, '0')}:00`;
}

/** An inclusive-start, exclusive-end window as '11:00–14:00' (en dash, matching fmtRange). */
export function hourSpan(start: number, end: number): string {
  return `${hourLabel(start)}–${hourLabel(end)}`;
}

interface HourWindow {
  start: number;
  end: number;
  total: number;
}

/** The contiguous `span`-hour window with the highest sum; ties go to the earliest. */
function bestSumWindow(values: readonly number[], span: number): HourWindow {
  let best: HourWindow = { start: 0, end: span, total: -1 };
  let running = values.slice(0, span).reduce((a, b) => a + b, 0);
  for (let start = 0; start + span <= values.length; start += 1) {
    if (start > 0) running += (values[start + span - 1] ?? 0) - (values[start - 1] ?? 0);
    if (running > best.total) best = { start, end: start + span, total: running };
  }
  return best;
}

/** Pull a window's edges in to the nearest hour with a value, so a named stretch never opens or closes empty. */
function trim(win: HourWindow, values: readonly number[]): HourWindow {
  let { start, end } = win;
  while (start < end - 1 && (values[start] ?? 0) === 0) start += 1;
  while (end - 1 > start && (values[end - 1] ?? 0) === 0) end -= 1;
  return { ...win, start, end };
}

/**
 * The busiest contiguous stretch (up to three hours) of a plain hourly array, as '11:00–13:00', or null
 * when nothing happened or nothing stands out (a single active hour is always its own peak).
 */
export function peakSpan(values: readonly (number | null | undefined)[] | null | undefined): string | null {
  const v = Array.isArray(values) ? values.map(num) : [];
  const total = v.reduce((a, b) => a + b, 0);
  const active = v.filter((x) => x > 0).length;
  if (total <= 0) return null;

  const max = Math.max(...v);
  if (!(active <= 1 || max > (total / active) * PEAK_LIFT)) return null;

  const win = trim(bestSumWindow(v, Math.min(PEAK_SPAN, active)), v);
  return hourSpan(win.start, win.end);
}

/** One hour of `outcome_hours.hours`, made dense and non-negative. */
export interface OutcomeHourRow {
  h: number;
  positive: number;
  connected: number;
  cc: number;
}

/** The minimum an upstream hour row must look like. */
export interface OutcomeHourInput {
  hour?: number | null;
  positive?: number | null;
  connected?: number | null;
  cc?: number | null;
}

/**
 * `outcome_hours.hours` → 24 dense rows indexed by hour. A row without a usable `hour` falls back to its
 * position (LAE behaviour); an hour outside 0–23 is dropped rather than wrapped.
 */
export function outcomeRows(hours: readonly OutcomeHourInput[] | null | undefined): OutcomeHourRow[] {
  const out: OutcomeHourRow[] = Array.from({ length: HOURS_IN_DAY }, (_, h) => ({ h, positive: 0, connected: 0, cc: 0 }));
  (Array.isArray(hours) ? hours : []).forEach((r, i) => {
    const raw = r?.hour;
    const h = typeof raw === 'number' && Number.isInteger(raw) ? raw : i;
    if (h < 0 || h >= HOURS_IN_DAY) return;
    out[h] = { h, positive: num(r?.positive), connected: num(r?.connected), cc: num(r?.cc) };
  });
  return out;
}

export interface OutcomeWindow {
  start: number;
  end: number;
  /** Positive outcomes inside the window. */
  pos: number;
  /** pos ÷ connected inside the window (0 when nothing was answered). */
  rate: number;
  /** Whole-number percentage of `total`. */
  share: number;
}

/** Best contiguous `span`-hour window by positive outcomes; ties go to the higher outcome rate. */
function bestOutcomeOf(rows: readonly OutcomeHourRow[], span: number): Omit<OutcomeWindow, 'share'> | null {
  let best: Omit<OutcomeWindow, 'share'> | null = null;
  for (let s = 0; s + span <= rows.length; s += 1) {
    let pos = 0;
    let con = 0;
    for (let h = s; h < s + span; h += 1) {
      pos += rows[h]?.positive ?? 0;
      con += rows[h]?.connected ?? 0;
    }
    const rate = con ? pos / con : 0;
    if (!best || pos > best.pos || (pos === best.pos && rate > best.rate)) best = { start: s, end: s + span, pos, rate };
  }
  return best;
}

/**
 * The best 2- or 3-hour window of positive outcomes (the 3-hour one only when it holds ≥ 1.15× the
 * 2-hour one), with its share of `total`. null when there is nothing positive.
 */
export function bestOutcomeWindow(rows: readonly OutcomeHourRow[], total: number): OutcomeWindow | null {
  if (!(total > 0)) return null;
  const two = bestOutcomeOf(rows, 2);
  const three = bestOutcomeOf(rows, 3);
  if (!two) return null;
  const pick = three && three.pos >= two.pos * WIDEN_GAIN ? three : two;
  if (!(pick.pos > 0)) return null;
  return { ...pick, share: Math.round((pick.pos / total) * 100) };
}

/** Heatmap ramp step: 0 = empty, 1..5 = `ceil(v / max × 5)`. */
export function heatStep(v: number, max: number): number {
  if (!(v > 0) || !(max > 0)) return 0;
  return Math.min(5, Math.max(1, Math.ceil((v / max) * 5)));
}
